import * as qs from 'querystring';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useBrowser } from '../services/BrowserService';
import { asDynamic } from '../util/Dynamic';
import { env } from '../util/env';
import { useUserData } from './UserProvider';

const { MEGATRON_HOST, MOM_HOST } = env;

export enum LoginMethod {
  Email = 'email',
  Google = 'google',
  Facebook = 'facebook',
  Apple = 'apple',
  Signup = 'signup',
}

const SUCCESS_POPUP = '/members/login/popup/success';

const URLS = {
  [LoginMethod.Email]: `${SUCCESS_POPUP}?default`,
  [LoginMethod.Google]: `${MOM_HOST}/login/google/redirect?redirect_uri=${MEGATRON_HOST}${SUCCESS_POPUP}`,
  [LoginMethod.Facebook]: `${MOM_HOST}/login/facebook/redirect?redirect_uri=${MEGATRON_HOST}${SUCCESS_POPUP}`,
  [LoginMethod.Apple]: `${MOM_HOST}/apple-terms?redirect_uri=${MEGATRON_HOST}${SUCCESS_POPUP}`,
  [LoginMethod.Signup]: `${MOM_HOST}/register?redirect_uri=${MEGATRON_HOST}${SUCCESS_POPUP}`,
};

export type LoginContext = {
  practiceId?: string;
  display?: string;
  bookingFormClient?: string;
};

/**
 * @param callback? action to call after successful login
 */
export const useLogin = (method: LoginMethod, context?: LoginContext, callback?: VoidFunction) => {
  const { window } = useBrowser();
  const lang = useTranslation().i18n.language;
  const refetchUser = useUserData().refetch;

  const { whiteLabelProps } = useTheme();
  const isCBP = whiteLabelProps?.governmentFlow ?? false;

  const queryString = { lang, ...context, ...(isCBP ? { cid: 'par:hea:cbp::cbp::::mar21' } : {}) };

  const url: string = `${URLS[method]}&${qs.stringify(queryString)}`;

  return useCallback(() => {
    if (window) {
      window.open(url, 'he_login', 'width=600,height=800,toolbar=0,resizable=1,scrollbars=yes');

      window.onmessage = (e: MessageEvent) => {
        if (asDynamic(e.data)?.success) {
          if (window) {
            window.onmessage = null;
          }

          // trigger refresh of user context
          refetchUser?.();
          // call callback if set
          callback?.();
        }
      };
    }
  }, [url, callback, refetchUser, window]);
};
